import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Clock3, FileClock, Lightbulb, Plus, Upload, Users, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { GroupIcon } from "@/lib/groupIcons";

const tiles = [
  { key: "total", label: "Total insureds", icon: Users, tone: "text-slate-900", to: "/vendors" },
  { key: "compliant", label: "Compliant", icon: CheckCircle2, tone: "text-green-700", to: "/vendors?status=compliant" },
  { key: "non_compliant", label: "Non-Compliant", icon: AlertCircle, tone: "text-red-700", to: "/vendors?status=non_compliant" },
  { key: "pending_review", label: "Pending review", icon: FileClock, tone: "text-violet-700", to: "/vendors?status=pending_review" },
  { key: "expiring_soon", label: "Expiring ≤ 30 days", icon: Clock3, tone: "text-amber-700", to: "/vendors?status=expiring_soon" },
  { key: "expired", label: "Expired", icon: AlertCircle, tone: "text-red-800", to: "/vendors?status=expired" },
  { key: "invited", label: "Awaiting upload", icon: Upload, tone: "text-slate-600", to: "/vendors?status=invited" },
];

export default function InsuredDashboardPage() {
  const { user } = useAuth();
  const isDemo = user?.email?.endsWith("@coiguardian.demo");
  const [summary, setSummary] = useState(null);
  const [groupCount, setGroupCount] = useState(0);
  const [tipDismissed, setTipDismissed] = useState(() => sessionStorage.getItem("coi_pin_tip_dismissed") === "1");
  useEffect(() => { api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => setSummary({ total: 0, counts: {} })); api.get("/groups").then((r) => setGroupCount(r.data.length)).catch(() => {}); }, []);
  function dismissTip() { sessionStorage.setItem("coi_pin_tip_dismissed", "1"); setTipDismissed(true); }
  if (!summary) return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dashboard-loading-state">{Array.from({ length: 7 }).map((_, i) => <div className="h-32 animate-pulse rounded-lg border border-slate-200 bg-white" key={i} />)}</div>;
  if (!summary.total) return <section data-testid="dashboard-empty-state" className="py-8"><p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Your command center</p><h1 className="mt-3 text-3xl font-semibold text-slate-900">Know whose insurance evidence is current.</h1><p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">Start with an insured list. COIGuardian keeps requirements, contacts, and every compliance status in one dependable place.</p><Button asChild className="mt-8" data-testid="dashboard-add-vendor-button"><Link to="/vendors"><Plus size={17} />Add your first insured</Link></Button></section>;
  const dashboardGroups = summary.groups || [];
  const showPinTip = isDemo && !tipDismissed && groupCount > 0 && dashboardGroups.length === 0;
  return (
    <section data-testid="dashboard-page">
      <div><p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Compliance overview</p><h1 className="mt-2 text-2xl font-semibold text-slate-900">Dashboard</h1><p className="mt-1 text-sm text-slate-600">Your insurance compliance picture at a glance.</p></div>
      {showPinTip && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="dashboard-pin-tip">
          <p className="flex items-center gap-2 text-sm text-amber-900"><Lightbulb className="shrink-0 text-amber-600" size={17} /><span><span className="font-semibold">Tip:</span> Pin your busiest group to this dashboard. Open a group and turn on <span className="font-semibold">"Show on dashboard"</span> to see its compliance snapshot at a glance.</span></p>
          <div className="flex items-center gap-2">
            <Button asChild data-testid="dashboard-pin-tip-cta" size="sm"><Link to="/groups">Open groups</Link></Button>
            <Button aria-label="Dismiss tip" data-testid="dashboard-pin-tip-dismiss" onClick={dismissTip} size="icon" variant="ghost"><X size={16} /></Button>
          </div>
        </div>
      )}
      <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {tiles.map((t) => {
          const Icon = t.icon;
          const value = t.key === "total" ? summary.total : summary.counts[t.key] || 0;
          return (
            <Link data-testid={`dashboard-tile-${t.key}`} key={t.key} to={t.to}>
              <Card className="border-slate-200 shadow-none transition hover:border-blue-300 hover:shadow-sm">
                <CardContent className="p-4">
                  <Icon className={t.tone} size={17} />
                  <p className={`mt-5 text-3xl font-extrabold tabular-nums ${t.tone}`} data-testid={`dashboard-tile-value-${t.key}`}>{value}</p>
                  <p className="mt-1 text-xs font-medium text-slate-500">{t.label}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {dashboardGroups.length > 0 && (
        <div className="mt-10" data-testid="dashboard-groups">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">Pinned groups</h2>
            <Link className="text-xs font-semibold text-blue-700 hover:underline" to="/groups">Manage groups</Link>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {dashboardGroups.map((group) => (
              <Link data-testid={`dashboard-group-${group.id}`} key={group.id} to={`/groups/${group.id}`}>
                <Card className="border-slate-200 shadow-none transition hover:border-blue-300 hover:shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-700"><GroupIcon name={group.icon} size={19} /></div>
                      <h3 className="truncate font-semibold text-slate-900">{group.name}</h3>
                    </div>
                    <p className="mt-4 text-sm text-slate-700" data-testid={`dashboard-group-stats-${group.id}`}>
                      <span className="font-bold tabular-nums text-slate-900">{group.memberCount}</span> insured{group.memberCount === 1 ? "" : "s"}
                      <span className="text-slate-400"> · </span>
                      <span className="font-semibold text-green-700">{group.compliantCount} compliant</span>
                      <span className="text-slate-400"> · </span>
                      <span className={`font-semibold ${group.issuesCount ? "text-red-700" : "text-slate-500"}`}>{group.issuesCount} issue{group.issuesCount === 1 ? "" : "s"}</span>
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
